import { Button, Flex } from '@chakra-ui/react';
import React from 'react';

import type { ButtonItem, Placement } from '../../config';
import { useCodingScreen } from '../../interface';
import { readState } from '../../slots/readState';
import {
  TOPBAR_BG,
  TOPBAR_BORDER,
  TOPBAR_BORDER_STYLE,
  TOPBAR_BORDER_WIDTH,
  TOPBAR_H
} from '../../styles/tokens';
import { Slot } from '../Slot';

/** Visible items for one `placement` group, ascending by `order` within that group -- same
 *  placement-then-order contract as `ReviewTransactionsTabs`' `visibleActionsFor`, kept local to
 *  this molecule folder per that precedent. `.filter` on a `readonly ButtonItem[]` already returns
 *  a fresh mutable array, so `.sort` never mutates the config's own (readonly, `as const`) item
 *  list. */
function visibleItemsFor(
  items: readonly ButtonItem[],
  placement: Placement
): ButtonItem[] {
  return items
    .filter((item) => item.visible && item.placement === placement)
    .sort((a, b) => a.order - b.order);
}

/**
 * Top utility bar molecule. `header.topBar.visible` is `false` in
 * `DEFAULT_CODING_SCREEN_CONFIG` -- "built but hidden" per the ticket -- so by default this renders
 * IN the DOM, geometry-preserving-hidden (`visibility:hidden` + `aria-hidden`), never `display:none`.
 * Always stamps its `data-content-key` so future wiring/tests can find it whether hidden or visible.
 *
 * `header.topBar.items` (search/help/zoom/coder-progress controls, Phase 2) IS config-driven: every
 * visible item renders as a reused Chakra `Button` (`variant` from `item.variant`), grouped by
 * `placement` ('start' left, 'end' right) then sorted by `order` within each group -- INERT, no
 * `onClick`, mirroring `ReviewTransactionsTabs`' action-button contract exactly. Items render into
 * the DOM regardless of `topBar.visible` -- ONLY the container's `visibility`/`aria-hidden` toggle
 * above hides them, so "built but hidden" holds for a populated top bar too, not just the empty
 * default.
 *
 * `role="region"` + `aria-label="Top bar"` -- a `role="region"` landmark applies here the same as it
 * already did for every sibling header/footer bar
 * (`PatientSummaryBar`/`CaseContextBar`/`ShortcutStatusBar`/`PaneFooterSummary`/etc.); this
 * container was the one bar in that family missing it. The geometry-preserving hide contract is
 * unchanged by adding the landmark: `visibility:hidden` + `aria-hidden` still hide the region (and
 * its label) from the accessibility tree exactly as before, never `display:none`.
 *
 * Buttons are INTERACTIVE -- unlike the non-interactive Tag/Box hosts
 * elsewhere in this folder, a resolved `componentId` component is never nested INSIDE a `<Button>`
 * (nested interactive controls are invalid a11y); instead each item's `Slot` FALLBACK *is* the
 * `<Button>` itself (`data-slot-key` moves onto the `Button`, since there is no separate host
 * wrapper element here). When `item.componentId` resolves in the injected registry, the component
 * REPLACES the button entirely and owns its own a11y/label via `item.label`; when it doesn't (the
 * common case, including every empty-shell default), the fallback `<Button variant={item.variant}>`
 * renders `item.label` exactly as before -- a bound `contentId`/state value is passed to a resolved
 * component but never leaks into the plain button's own label.
 */
export const TopBarContainer: React.FC = () => {
  const { config, state } = useCodingScreen();
  const { mode } = config;
  const { contentKey, visible, items } = config.header.topBar;
  const startItems = visibleItemsFor(items, 'start');
  const endItems = visibleItemsFor(items, 'end');

  return (
    <Flex
      role="region"
      aria-label="Top bar"
      data-content-key={contentKey}
      aria-hidden={visible ? undefined : true}
      align="center"
      justify="space-between"
      bg={TOPBAR_BG}
      style={{
        height: TOPBAR_H,
        minHeight: TOPBAR_H,
        visibility: visible ? 'visible' : 'hidden',
        borderBottomWidth: TOPBAR_BORDER_WIDTH,
        borderBottomStyle: TOPBAR_BORDER_STYLE,
        borderBottomColor: TOPBAR_BORDER
      }}
    >
      <Flex gap={2}>
        {startItems.map((item) => (
          <Slot
            key={item.contentKey}
            item={item}
            value={readState(state, item.contentId)}
            mode={mode}
            fallback={() => (
              <Button data-slot-key={item.contentKey} variant={item.variant}>
                {item.label}
              </Button>
            )}
          />
        ))}
      </Flex>
      <Flex gap={2}>
        {endItems.map((item) => (
          <Slot
            key={item.contentKey}
            item={item}
            value={readState(state, item.contentId)}
            mode={mode}
            fallback={() => (
              <Button data-slot-key={item.contentKey} variant={item.variant}>
                {item.label}
              </Button>
            )}
          />
        ))}
      </Flex>
    </Flex>
  );
};
